import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Flex,
  Box,
  HStack,
  IconButton,
  Text,
  VStack,
  Menu,
  MenuButton,
  MenuList,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  useDisclosure,
} from '@chakra-ui/react';
import { HamburgerIcon, ChevronLeftIcon } from '@chakra-ui/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { apiMeClaims, apiGet } from '../lib/api';
import { logout, getCurrentUserHighestRole } from '../auth/keycloak';
import { API_CONFIG } from '../config/env';
import GlobalSearchBar from './GlobalSearchBar';
import SidebarContent from './layout/SidebarContent';
import AppHeader from './layout/AppHeader';
import NotificationBell from './notifications/NotificationBell';
import { cleanupPushBeforeLogout } from '../lib/notificationPush';

export default function AppLayout({ children }: { children: React.ReactNode }): JSX.Element {
  const { t, i18n } = useTranslation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isSearchModalOpen,
    onOpen: onSearchModalOpen,
    onClose: onSearchModalClose,
  } = useDisclosure();
  const mobileSearchInputRef = useRef<HTMLInputElement>(null);
  const [collapsed, setCollapsed] = useState(true);
  const [isPinned, setIsPinned] = useState(false);
  const COLLAPSED_WIDTH = 80;

  const [role, setRole] = useState<'ADMIN' | 'TEACHER' | 'STUDENT' | 'UNKNOWN'>(() => {
    try {
      return (localStorage.getItem('role') as any) || 'UNKNOWN';
    } catch {
      return 'UNKNOWN';
    }
  });
  const navigate = useNavigate();
  const location = useLocation();

  const isHomePage = location.pathname === '/';

  const isVi = (i18n.language || 'vi').startsWith('vi');
  const changeLang = useCallback(
    (l: 'vi' | 'en') => {
      i18n.changeLanguage(l);
      try {
        localStorage.setItem('lng', l);
      } catch {}
    },
    [i18n],
  );

  const { data: userData } = useQuery({
    queryKey: ['user-me'],
    queryFn: () =>
      apiGet<{ id: number; fullName?: string; pictureUrl?: string; email?: string }>('/api/me'),
    staleTime: 5 * 60 * 1000,
  });

  const { data: avatarData } = useQuery({
    queryKey: ['user-avatar'],
    queryFn: () => apiGet<{ avatarId: number | null }>('/api/profile/avatar'),
    staleTime: 5 * 60 * 1000,
  });

  const getAvatarUrl = () => {
    if (avatarData?.avatarId) {
      return `${API_CONFIG.baseUrl}/api/avatars/${avatarData.avatarId}`;
    }
    return userData?.pictureUrl || undefined;
  };

  function handleSidebarToggle() {
    if (isPinned) {
      setIsPinned(false);
      setCollapsed(true);
    } else {
      setIsPinned(true);
      setCollapsed(false);
    }
  }

  function handleLogout() {
    // Best-effort, time-bounded push cleanup (revoke own binding, unsubscribe
    // locally, drop cached OS notifications) then logout regardless of network.
    void cleanupPushBeforeLogout().finally(() => logout());
  }

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        if (window.innerWidth < 768 && !isHomePage) {
          e.preventDefault();
          onSearchModalOpen();
          setTimeout(() => mobileSearchInputRef.current?.focus(), 100);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isHomePage, onSearchModalOpen]);

  useEffect(() => {
    const handleNavigate = (event: Event) => {
      const customEvent = event as CustomEvent;
      const { to, options } = customEvent.detail;
      if (to) {
        navigate(to, options);
      }
    };

    window.addEventListener('navigate', handleNavigate);

    return () => {
      window.removeEventListener('navigate', handleNavigate);
    };
  }, [navigate]);

  useEffect(() => {
    let mounted = true;

    const keycloakRole = getCurrentUserHighestRole();
    if (keycloakRole !== 'UNKNOWN') {
      setRole(keycloakRole);
      try {
        localStorage.setItem('role', keycloakRole);
      } catch {}
      return;
    }

    apiMeClaims()
      .then((c) => {
        if (!mounted) return;
        setRole(c.role as any);
        try {
          localStorage.setItem('role', c.role);
        } catch {}
      })
      .catch(() => setRole('STUDENT'));
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <Flex
      minH="100vh"
      maxH="100vh"
      bg="white"
      overflow="hidden"
      maxW="100vw"
      w="100vw"
      position="relative"
    >
      <Box
        display={{ base: 'none', md: 'block' }}
        position={isPinned ? 'relative' : 'absolute'}
        left="0"
        top="0"
        bottom="0"
        zIndex={isPinned ? 1 : 1001}
        onMouseEnter={() => {
          if (!isPinned) {
            setCollapsed(false);
          }
        }}
        onMouseLeave={() => {
          if (!isPinned) {
            setCollapsed(true);
          }
        }}
      >
        <SidebarContent
          collapsed={collapsed}
          onToggle={handleSidebarToggle}
          onNavigate={(path) => navigate(path)}
          role={role}
          isPinned={isPinned}
        />
      </Box>

      <Menu isOpen={isOpen} onClose={onClose} placement="bottom-start">
        <MenuButton
          as={IconButton}
          aria-label={t('common.openMenu')}
          icon={<HamburgerIcon />}
          display={{ base: 'inline-flex', md: 'none' }}
          size="sm"
          position="fixed"
          top="10px"
          left="10px"
          zIndex={1500}
          onClick={onOpen}
        />
        <MenuList maxH="80vh" overflowY="auto" minW="280px" maxW="90vw" zIndex={1500}>
          <Box p={2}>
            <SidebarContent
              collapsed={false}
              onToggle={onClose}
              onNavigate={(path) => {
                onClose();
                navigate(path);
              }}
              role={role}
              isPinned={true}
            />
          </Box>
        </MenuList>
      </Menu>

      <Flex
        direction="column"
        flex="1"
        minW={0}
        maxW="100%"
        position="relative"
        ml={{ base: 0, md: isPinned ? 0 : `${COLLAPSED_WIDTH}px` }}
      >
        <AppHeader
          userData={userData}
          avatarUrl={getAvatarUrl()}
          role={role}
          isHomePage={isHomePage}
          onLogout={handleLogout}
          onSearchOpen={onSearchModalOpen}
          onLanguageChange={changeLang}
          isVi={isVi}
          notificationSlot={<NotificationBell />}
        />

        <Box
          flex="1"
          px={0}
          py={0}
          overflowX="hidden"
          overflowY="auto"
          maxW="100%"
          w="100%"
          minW={0}
          position="relative"
        >
          {children}
        </Box>
      </Flex>

      <Modal isOpen={isSearchModalOpen} onClose={onSearchModalClose} size="full">
        <ModalOverlay />
        <ModalContent m={0} borderRadius={0}>
          <ModalBody p={4}>
            <VStack spacing={4} align="stretch">
              <HStack justify="space-between">
                <Text fontWeight="bold">{t('search.placeholder')}</Text>
                <IconButton
                  aria-label={t('common.close')}
                  icon={<ChevronLeftIcon />}
                  size="sm"
                  variant="ghost"
                  onClick={onSearchModalClose}
                />
              </HStack>
              <GlobalSearchBar
                size="lg"
                maxWidth="100%"
                showShortcut={false}
                autoFocus={true}
                onResultSelect={() => onSearchModalClose()}
              />
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Flex>
  );
}
